import React from 'react';
import { 
    ChevronLeft, 
    ChevronRight, 
    Plus, 
    Settings
} from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine } from 'recharts';
import { useAuthoritativeTradesRecords } from '../../hooks/useAuthoritativeTrades';
import {
    filterTradesByDateKey,
    getTradePnl,
    getTradeTimestampMs,
    getTradeVolume,
    normalizeTradeSide,
    normalizeTradeStatus,
    type AnyTrade,
} from '../../lib/trades/authoritativeTradeData';
import { calculateWinRatePercent } from '../../lib/trades/winRate';
import { formatDateTimeInTimezone } from '../../lib/time/displayTimezone';
import { useDisplayTimezone } from '../app/DisplayTimezoneProvider';
import RemovableTag from '../ui/RemovableTag';
import {
    loadJournalEntryTagOverrides,
    saveJournalEntryTagOverrides,
    type JournalEntryTagOverrides,
} from './journalEntryTagOverrides';
import { useAssetClassVisibility } from '../../lib/assetClassVisibility';

const FALLBACK_DATE_LABEL = 'Monday, Dec 15, 2025';
const MAX_VISIBLE_COLUMNS = 12;
const COLUMN_WIDTH_STORAGE_KEY = 'tradeproof:journal-entry:column-widths:v1';
const COLUMN_ORDER_STORAGE_KEY = 'tradeproof:journal-entry:column-order:v1';
const JOURNAL_TABLE_MIN_COLUMN_WIDTH = 88;

type JournalDropIndicatorState = {
    index: number;
    left: number;
};

type ColumnDragState = {
    id: string;
    lastClientX: number;
};

function getStartOfDayLocal(d: Date) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function toDateKey(value: Date) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function fmtMoney(amount: number) {
    const safe = Number.isFinite(amount) ? Number(amount) : 0;
    return safe.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function fmtTime24hWithSeconds(ms: number, timeZone: string) {
    if (!Number.isFinite(ms)) return '—';
    const d = new Date(ms);
    if (!Number.isFinite(d.getTime())) return '—';
    return formatDateTimeInTimezone(ms, timeZone).timeStr;
}

function fmtDurationMinutes(durationMinutes?: number) {
    if (!Number.isFinite(durationMinutes)) return '—';
    const mins = Math.max(0, Math.round(Number(durationMinutes)));
    if (mins < 60) return `${mins}m`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m ? `${h}h ${m}m` : `${h}h`;
}

// --- HELPER COMPONENTS ---

const MetricCard: React.FC<{title: string, value: string | number, subtext: string, isNegative: boolean, isCurrency?: boolean}> = ({ title, value, subtext, isNegative, isCurrency = true }) => {
    const colorClass = isNegative ? 'text-red-500' : 'text-green-500';
    const renderValue = () => {
        if (typeof value === 'number') {
            if (isCurrency) return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
            return value.toLocaleString('en-US');
        }
        return value;
    };

    return (
        <div className="bg-white dark:bg-[#1a1a1c] p-4 rounded-xl border border-zinc-200 dark:border-gray-800 shadow-md">
            <div className="text-sm font-medium text-zinc-600 dark:text-gray-400 mb-2">{title}</div>
            <div className={`text-3xl font-bold ${colorClass}`}>{renderValue()}</div>
            <div className="text-sm text-zinc-500 dark:text-gray-400 mt-1">{subtext}</div>
        </div>
    );
};

const ProgressRing: React.FC<{ percentage: number, color: string, label: string }> = ({ percentage, color, label }) => {
    const radius = 40;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percentage / 100) * circumference;

    return (
        <div className="flex flex-col items-center">
            <svg width="90" height="90" viewBox="0 0 90 90" className="-rotate-90">
                <circle
                    className="text-zinc-300 dark:text-gray-700"
                    strokeWidth="8"
                    stroke="currentColor"
                    fill="transparent"
                    r={radius}
                    cx="45"
                    cy="45"
                />
                <circle
                    className={color}
                    strokeWidth="8"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    stroke="currentColor"
                    fill="transparent"
                    r={radius}
                    cx="45"
                    cy="45"
                />
            </svg>
            <div className="absolute flex items-center justify-center h-[90px] text-lg font-bold">
                {percentage.toFixed(0)}%
            </div>
            <div className="text-sm text-zinc-600 dark:text-gray-400 mt-2">{label}</div>
        </div>
    );
};

const LockIcon = ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
    </svg>
);
const XIcon = ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
);


// --- MAIN DASHBOARD COMPONENT ---

type JournalEntryPageProps = {
    date?: Date | string;
    onNavigateDate?: (d: Date) => void;
    isNewEntry?: boolean;
    onCreateNew?: () => void;
    focusSymbol?: string;
};

const JournalEntryPage: React.FC<JournalEntryPageProps> = ({ date: propDate, onNavigateDate, isNewEntry, focusSymbol }) => {
    const authoritativeTrades = useAuthoritativeTradesRecords();
    const displayTimeZone = useDisplayTimezone();

    const { headerDate, baseDate } = React.useMemo(() => {
        const d = typeof propDate === 'string' ? new Date(propDate) : (propDate ?? null);
        const safe = d && Number.isFinite(d.getTime()) ? d : null;
        const header = safe
            ? safe.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric', timeZone: displayTimeZone })
            : FALLBACK_DATE_LABEL;
        return { headerDate: header, baseDate: safe };
    }, [propDate, displayTimeZone]);

    const canNavigateByDate = Boolean(onNavigateDate && baseDate);
    const shiftDate = (d: Date, deltaDays: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + deltaDays);

    const dayTrades = React.useMemo(() => {
        if (!baseDate) return [] as AnyTrade[];
        const dateKey = toDateKey(baseDate);
        const symbolFilter = (focusSymbol || '').trim().toUpperCase();
        return filterTradesByDateKey(authoritativeTrades, dateKey)
            .filter((trade) => {
                if (!symbolFilter) return true;
                const sym = String(trade.symbol || '').trim().toUpperCase();
                return sym === symbolFilter;
            })
            .slice()
            .sort((left, right) => (getTradeTimestampMs(left) ?? 0) - (getTradeTimestampMs(right) ?? 0));
    }, [authoritativeTrades, baseDate, focusSymbol]);

    // cumulativeSeries and numeric metrics are computed from the combined `rows` (imported + custom)
    // to ensure fees/commission provided on custom entries are included. `rows` is defined below.

    const [visibility] = useAssetClassVisibility();
    const isStocksOnly = visibility.STOCKS && !visibility.FUTURES && !visibility.FOREX && !visibility.CRYPTO;

    type TableColumnId = string;
    type TableColumn = { id: string; label: string; locked?: boolean; assetClass?: 'STOCKS' | 'FUTURES' | 'FOREX' | 'CRYPTO' };
    const ALL_COLUMNS: TableColumn[] = React.useMemo(() => [
        { id: 'time', label: 'Time' },
        { id: 'symbol', label: 'Symbol' },
        { id: 'volume', label: 'Quantity' },
        { id: 'executionCount', label: 'Executions' },
        { id: 'side', label: 'Side' },
        { id: 'status', label: 'Status' },
        { id: 'pnl', label: 'P&L' },
        { id: 'pnlR', label: 'P&L (R)', locked: true },
        { id: 'pnlPercent', label: isStocksOnly ? 'P&L (%)' : 'P&L (%, equities only)', assetClass: 'STOCKS' },
        { id: 'pnlTicksGross', label: 'P&L (ticks, gross, futures only)', assetClass: 'FUTURES' },
        { id: 'pnlTicksContract', label: 'P&L (ticks per contract, gross, futures only)', assetClass: 'FUTURES' },
        { id: 'commission', label: 'Commission', locked: true },
        { id: 'commissionAndFees', label: 'Fees' },
        { id: 'initialRisk', label: 'Initial Risk', locked: true },
        { id: 'positionMfe', label: 'Position MFE', locked: true },
        { id: 'positionMae', label: 'Position MAE', locked: true },
        { id: 'priceMfe', label: 'Price MFE', locked: true },
        { id: 'priceMae', label: 'Price MAE', locked: true },
        { id: 'bestExitPnl', label: 'Best Exit P&L', locked: true },
        { id: 'exitEfficiency', label: 'Exit Efficiency (%, winners only)', locked: true },
        { id: 'duration', label: 'Duration' },
        { id: 'shared', label: 'Shared' },
        { id: 'notes', label: 'Notes' },
        { id: 'tags', label: 'Tags' }
    ], [isStocksOnly]);

    const isColumnSupported = React.useCallback((col: TableColumn) => {
        if (col.assetClass === 'FUTURES' && !visibility.FUTURES) return false;
        if (col.assetClass === 'FOREX' && !visibility.FOREX) return false;
        if (col.assetClass === 'CRYPTO' && !visibility.CRYPTO) return false;
        return true;
    }, [visibility]);
    const DEFAULT_COLUMNS = ['time', 'symbol', 'side', 'status', 'duration', 'volume', 'commissionAndFees', 'pnl', 'tags'];
    const normalizeColumnOrder = (input: unknown): string[] => {
        const validIds = new Set(ALL_COLUMNS.map((column) => column.id));
        const incoming = Array.isArray(input) ? input : [];
        const seen = new Set<string>();
        const ordered = incoming
            .map((value) => String(value ?? '').trim())
            .filter((id) => {
                if (!validIds.has(id) || seen.has(id)) return false;
                seen.add(id);
                return true;
            });

        for (const columnId of DEFAULT_COLUMNS) {
            if (!seen.has(columnId)) ordered.push(columnId);
        }

        for (const column of ALL_COLUMNS) {
            if (!seen.has(column.id)) ordered.push(column.id);
        }

        return ordered;
    };

    const [visibleColumns, setVisibleColumns] = React.useState<TableColumnId[]>(() => DEFAULT_COLUMNS.slice());
    const [columnOrder, setColumnOrder] = React.useState<TableColumnId[]>(() => {
        if (typeof window === 'undefined') return normalizeColumnOrder(null);
        try {
            const raw = window.localStorage.getItem(COLUMN_ORDER_STORAGE_KEY);
            return normalizeColumnOrder(raw ? JSON.parse(raw) : null);
        } catch {
            return normalizeColumnOrder(null);
        }
    });
    const tableColumns = React.useMemo(
        () => columnOrder
            .map((id) => (visibleColumns.includes(id) ? ALL_COLUMNS.find((c) => c.id === id) : null))
            .filter((c): c is TableColumn => c != null && isColumnSupported(c)),
        [columnOrder, visibleColumns, ALL_COLUMNS, isColumnSupported]
    );

    const [isColumnSettingsOpen, setIsColumnSettingsOpen] = React.useState(false);
    const [isCustomEntryOpen, setIsCustomEntryOpen] = React.useState(false);
    const [columnWidths, setColumnWidths] = React.useState<Record<string, number>>(() => {
        if (typeof window === 'undefined') return {};
        try {
            const raw = window.localStorage.getItem(COLUMN_WIDTH_STORAGE_KEY);
            return raw ? JSON.parse(raw) : {};
        } catch {
            return {};
        }
    });
    const [tableWidthPx, setTableWidthPx] = React.useState<number | null>(null);
    const tableScrollerRef = React.useRef<HTMLDivElement | null>(null);
    const dragColumnIdRef = React.useRef<string | null>(null);
    const pointerDragRef = React.useRef<ColumnDragState | null>(null);
    const [draggingColumnId, setDraggingColumnId] = React.useState<string | null>(null);
    const [dropColumnId, setDropColumnId] = React.useState<string | null>(null);
    const [dropIndicator, setDropIndicator] = React.useState<JournalDropIndicatorState | null>(null);

    const [isDark, setIsDark] = React.useState(() => {
        if (typeof document === 'undefined') return false;
        return document.documentElement.classList.contains('dark');
    });

    React.useEffect(() => {
        if (typeof document === 'undefined') return;
        const root = document.documentElement;
        const obs = new MutationObserver(() => setIsDark(root.classList.contains('dark')));
        obs.observe(root, { attributes: true, attributeFilter: ['class'] });
        return () => obs.disconnect();
    }, []);

    React.useEffect(() => {
        if (typeof window === 'undefined') return;
        try {
            window.localStorage.setItem(COLUMN_WIDTH_STORAGE_KEY, JSON.stringify(columnWidths));
        } catch {}
    }, [columnWidths]);

    React.useEffect(() => {
        if (typeof window === 'undefined') return;
        try {
            window.localStorage.setItem(COLUMN_ORDER_STORAGE_KEY, JSON.stringify(columnOrder));
        } catch {}
    }, [columnOrder]);

    const headerRefs = React.useRef<Record<string, HTMLTableCellElement | null>>({});
    const resizingRef = React.useRef<{ id: string; startX: number; startWidth: number; startTableWidth: number } | null>(null);

    const lastAutoOpenKeyRef = React.useRef<number | null>(null);

    React.useEffect(() => {
        if (!isNewEntry) {
            lastAutoOpenKeyRef.current = null;
            return;
        }
        if (!baseDate) return;

        const key = baseDate.getTime();
        if (lastAutoOpenKeyRef.current === key) return;
        lastAutoOpenKeyRef.current = key;
        setIsCustomEntryOpen(true);
    }, [isNewEntry, baseDate]);

    type CustomRow = {
        id: string;
        timestamp: number;
        symbol: string;
        volume: number;
        side: 'Long' | 'Short' | '';
        pnl: number;
        durationMinutes?: number;
        fees?: number;
        commission?: number;
        notes?: string;
    };

    const [customRows, setCustomRows] = React.useState<CustomRow[]>([]);
    const [rowTagOverrides, setRowTagOverrides] = React.useState<JournalEntryTagOverrides>(() => loadJournalEntryTagOverrides());

    React.useEffect(() => {
        saveJournalEntryTagOverrides(rowTagOverrides);
    }, [rowTagOverrides]);

    const rows = React.useMemo(() => {
        const resolveTags = (rowKey: string, tags: unknown) => {
            const baseTags = Array.isArray(tags) ? tags.map((tag: unknown) => String(tag).trim()).filter(Boolean) : [];
            if (Object.prototype.hasOwnProperty.call(rowTagOverrides, rowKey)) {
                return Array.isArray(rowTagOverrides[rowKey]) ? rowTagOverrides[rowKey] : [];
            }
            return baseTags;
        };

        const importedRows = dayTrades.map((t) => {
            const vol = getTradeVolume(t);
            const pnl = getTradePnl(t);
            const normalizedSide = normalizeTradeSide(t);
            const side = (normalizedSide === 'LONG'
                ? 'Long'
                : normalizedSide === 'SHORT'
                    ? 'Short'
                    : (pnl >= 0 ? 'Long' : 'Short')) as 'Long' | 'Short';
            const timestamp = getTradeTimestampMs(t) ?? (Number(t.timestamp) || 0);

            return {
                key: String(t.storageId ?? t.id ?? timestamp),
                timestamp,
                symbol: t.symbol,
                volume: Math.abs(Math.round(Number(vol) || 0)),
                side,
                status: normalizeTradeStatus(t),
                pnl,
                fees: Number.isFinite((t as any).fees) ? Number((t as any).fees) : 0,
                commission: Number.isFinite((t as any).commission) ? Number((t as any).commission) : 0,
                durationMinutes: Number.isFinite(t.durationMinutes) ? Number(t.durationMinutes) : undefined,
                notes: '',
                tags: resolveTags(String(t.storageId ?? t.id ?? timestamp), (t as any).tags),
            };
        });

        const merged = [...importedRows, ...customRows.map((c) => ({
            key: c.id,
            timestamp: c.timestamp,
            symbol: c.symbol,
            volume: c.volume,
            side: (c.side || (c.pnl >= 0 ? 'Long' : 'Short')) as 'Long' | 'Short',
            status: 'closed' as const,
            pnl: c.pnl,
            fees: Number.isFinite((c as any).fees) ? Number((c as any).fees) : 0,
            commission: Number.isFinite((c as any).commission) ? Number((c as any).commission) : 0,
            durationMinutes: c.durationMinutes,
            notes: c.notes || '',
            tags: resolveTags(c.id, (c as any).tags),
        }))];

        merged.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
        return merged;
    }, [dayTrades, customRows, rowTagOverrides]);

    const handleRemoveTag = React.useCallback((rowKey: string, tagToRemove: string) => {
        const trimmed = String(tagToRemove ?? '').trim();
        if (!trimmed) return;
        setRowTagOverrides((prev) => {
            const existing = Object.prototype.hasOwnProperty.call(prev, rowKey)
                ? (Array.isArray(prev[rowKey]) ? prev[rowKey] : [])
                : (rows.find((row) => row.key === rowKey)?.tags ?? []);
            const nextTags = existing.filter((tag) => tag !== trimmed);
            return {
                ...prev,
                [rowKey]: nextTags,
            };
        });
    }, [rows]);

    const startColumnResize = (e: React.MouseEvent<HTMLDivElement>, id: string) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const frozenWidths = tableColumns.reduce((acc, col) => {
            const measured = headerRefs.current[col.id]?.getBoundingClientRect().width;
            acc[col.id] = Math.max(JOURNAL_TABLE_MIN_COLUMN_WIDTH, Math.round(measured || columnWidths[col.id] || 160));
            return acc;
        }, {} as Record<string, number>);
        const startWidth = frozenWidths[id] || columnWidths[id] || 160;
        const startTableWidth = tableColumns.reduce((sum, col) => sum + (frozenWidths[col.id] || 160), 0);
        setColumnWidths((prev) => ({ ...prev, ...frozenWidths }));
        setTableWidthPx(startTableWidth);
        resizingRef.current = { id, startX, startWidth, startTableWidth };
        const onMove = (ev: MouseEvent) => {
            if (!resizingRef.current) return;
            const delta = ev.clientX - resizingRef.current.startX;
            const next = Math.max(JOURNAL_TABLE_MIN_COLUMN_WIDTH, Math.round(resizingRef.current.startWidth + delta));
            const tableDelta = next - resizingRef.current.startWidth;
            setTableWidthPx(Math.max(200, Math.round(resizingRef.current.startTableWidth + tableDelta)));
            setColumnWidths((prev) => ({ ...prev, [resizingRef.current!.id]: next }));
        };
        const onUp = () => {
            resizingRef.current = null;
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    const reorderColumn = React.useCallback((activeId: string, overId: string) => {
        if (!activeId || !overId || activeId === overId) return;
        setColumnOrder((prev) => {
            const visibleOrder = prev.filter((id) => visibleColumns.includes(id));
            const oldIndex = visibleOrder.indexOf(activeId);
            const newIndex = visibleOrder.indexOf(overId);
            if (oldIndex < 0 || newIndex < 0) return prev;

            const nextVisible = visibleOrder.slice();
            const [moved] = nextVisible.splice(oldIndex, 1);
            nextVisible.splice(newIndex, 0, moved);

            const visibleSet = new Set(visibleOrder);
            let nextIndex = 0;
            return prev.map((id) => {
                if (!visibleSet.has(id)) return id;
                return nextVisible[nextIndex++] ?? id;
            });
        });
    }, [visibleColumns]);

    const reorderColumnAtIndex = React.useCallback((activeId: string, targetIndex: number) => {
        if (!activeId) return;
        setColumnOrder((prev) => {
            const visibleOrder = prev.filter((id) => visibleColumns.includes(id));
            const fromIndex = visibleOrder.indexOf(activeId);
            if (fromIndex < 0) return prev;

            const withoutActive = visibleOrder.filter((id) => id !== activeId);
            const boundedIndex = Math.max(0, Math.min(targetIndex, withoutActive.length));
            const nextVisible = withoutActive.slice();
            nextVisible.splice(boundedIndex, 0, activeId);

            const visibleSet = new Set(visibleOrder);
            let nextIndex = 0;
            return prev.map((id) => {
                if (!visibleSet.has(id)) return id;
                return nextVisible[nextIndex++] ?? id;
            });
        });
    }, [visibleColumns]);

    const computeDropIndicator = React.useCallback((overId: string, clientX: number): JournalDropIndicatorState | null => {
        const scroller = tableScrollerRef.current;
        if (!scroller) return null;

        const orderedIds = columnOrder.filter((id) => visibleColumns.includes(id));
        const overIndex = orderedIds.indexOf(overId);
        if (overIndex < 0) return null;

        const overEl = headerRefs.current[overId];
        if (!overEl) return null;

        const overRect = overEl.getBoundingClientRect();
        const insertAfter = clientX > overRect.left + overRect.width / 2;
        const insertionIndex = insertAfter ? overIndex + 1 : overIndex;

        const scrollerRect = scroller.getBoundingClientRect();
        let left = 0;
        if (insertionIndex >= orderedIds.length) {
            const lastId = orderedIds[orderedIds.length - 1];
            const lastEl = lastId ? headerRefs.current[lastId] : null;
            if (!lastEl) return null;
            left = lastEl.getBoundingClientRect().right - scrollerRect.left + scroller.scrollLeft;
        } else {
            const targetId = orderedIds[insertionIndex];
            const targetEl = targetId ? headerRefs.current[targetId] : null;
            if (!targetEl) return null;
            left = targetEl.getBoundingClientRect().left - scrollerRect.left + scroller.scrollLeft;
        }

        return { index: insertionIndex, left: Math.round(left) };
    }, [columnOrder, visibleColumns]);

    const getColumnIdFromClientX = React.useCallback((clientX: number): string | null => {
        const orderedIds = columnOrder.filter((id) => visibleColumns.includes(id));
        if (!orderedIds.length) return null;

        let nearestId = orderedIds[0] ?? null;
        let nearestDistance = Number.POSITIVE_INFINITY;

        for (const id of orderedIds) {
            const el = headerRefs.current[id];
            if (!el) continue;
            const rect = el.getBoundingClientRect();
            if (clientX >= rect.left && clientX <= rect.right) return id;
            const distance = Math.min(Math.abs(clientX - rect.left), Math.abs(clientX - rect.right));
            if (distance < nearestDistance) {
                nearestDistance = distance;
                nearestId = id;
            }
        }

        return nearestId;
    }, [columnOrder, visibleColumns]);

    const endPointerColumnDrag = React.useCallback(() => {
        const current = pointerDragRef.current;
        if (current) {
            const overId = getColumnIdFromClientX(current.lastClientX);
            if (overId && overId !== current.id) {
                const indicator = computeDropIndicator(overId, current.lastClientX) ?? dropIndicator;
                if (indicator) reorderColumnAtIndex(current.id, indicator.index);
                else reorderColumn(current.id, overId);
            }
        }

        pointerDragRef.current = null;
        dragColumnIdRef.current = null;
        setDraggingColumnId(null);
        setDropColumnId(null);
        setDropIndicator(null);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
    }, [computeDropIndicator, dropIndicator, getColumnIdFromClientX, reorderColumn, reorderColumnAtIndex]);

    const startPointerColumnDrag = React.useCallback((event: React.MouseEvent<HTMLDivElement>, id: string) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();

        dragColumnIdRef.current = id;
        pointerDragRef.current = { id, lastClientX: event.clientX };
        setDraggingColumnId(id);
        setDropColumnId(null);
        setDropIndicator(null);
        document.body.style.cursor = 'grabbing';
        document.body.style.userSelect = 'none';

        const onMove = (moveEvent: MouseEvent) => {
            const current = pointerDragRef.current;
            if (!current) return;
            current.lastClientX = moveEvent.clientX;
            const overId = getColumnIdFromClientX(moveEvent.clientX);
            if (!overId || overId === current.id) {
                setDropColumnId(null);
                setDropIndicator(null);
                return;
            }
            setDropColumnId(overId);
            setDropIndicator(computeDropIndicator(overId, moveEvent.clientX));
        };

        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            endPointerColumnDrag();
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }, [computeDropIndicator, endPointerColumnDrag, getColumnIdFromClientX]);

    const JournalHeader = ({ col }: { col: TableColumn }) => {
        const style: React.CSSProperties = {
            width: columnWidths[col.id] ? `${columnWidths[col.id]}px` : undefined,
        };

        return (
            <th
                key={col.id}
                scope="col"
                ref={(el) => { headerRefs.current[col.id] = el; }}
                style={style}
                className={`relative px-4 py-3 text-center font-medium transition-colors ${draggingColumnId === col.id ? 'opacity-60' : ''} ${dropColumnId === col.id ? (isDark ? 'bg-blue-900/30' : 'bg-blue-50') : (isDark ? 'bg-zinc-800' : 'bg-zinc-200')} ${isDark ? 'text-zinc-200 border-r border-b border-zinc-700 last:border-r-0' : 'text-zinc-700 border-r border-b border-zinc-300 last:border-r-0'}`}
            >
                <div
                    onMouseDown={(event) => startPointerColumnDrag(event, col.id)}
                    className="flex cursor-grab items-center justify-center gap-2 min-w-0 active:cursor-grabbing"
                >
                    <span className="truncate">{col.label.split('(')[0]}</span>
                </div>
                <div
                    onMouseDown={(e) => startColumnResize(e, col.id)}
                    onClick={(e) => e.stopPropagation()}
                    role="separator"
                    aria-orientation="vertical"
                    title="Resize column"
                    className="absolute right-0 top-0 h-full w-3 cursor-col-resize select-none"
                >
                    <div className="absolute right-0 top-0 h-full w-px bg-transparent" />
                </div>
            </th>
        );
    };

    // --- Compute cumulative series and metrics from the unified `rows` array ---
    const cumulativeSeries = React.useMemo(() => {
        let running = 0;
        return rows.map((r, idx) => {
            const pnl = Number.isFinite((r as any).pnl) ? Number((r as any).pnl) : 0;
            running += pnl;
            return { trade: idx + 1, pnl, cumulative: Number(running.toFixed(2)) };
        });
    }, [rows]);

    const grossPnl = React.useMemo(() => rows.reduce((acc, r) => acc + (Number.isFinite((r as any).pnl) ? Number((r as any).pnl) : 0), 0), [rows]);
    const fees = React.useMemo(() => rows.reduce((acc, r) => acc + (Number.isFinite((r as any).fees) ? Number((r as any).fees) : 0), 0), [rows]);
    const commission = React.useMemo(() => rows.reduce((acc, r) => acc + (Number.isFinite((r as any).commission) ? Number((r as any).commission) : 0), 0), [rows]);
    const netPnl = grossPnl - fees - commission;
    const totalTrades = rows.length;
    const winners = React.useMemo(() => rows.filter(r => Number.isFinite((r as any).pnl) && Number((r as any).pnl) > 0).length, [rows]);
    const losers = React.useMemo(() => rows.filter(r => Number.isFinite((r as any).pnl) && Number((r as any).pnl) < 0).length, [rows]);
    const winRate = calculateWinRatePercent(winners, losers);
    const lossRate = calculateWinRatePercent(losers, winners);
    const isNetPnlNegative = netPnl < 0;
    const volume = React.useMemo(() => rows.reduce((acc, r) => acc + (Number.isFinite((r as any).volume) ? Number((r as any).volume) : 0), 0), [rows]);
    const maxDrawdown = React.useMemo(() => {
        let peak = 0;
        let maxDd = 0;
        for (const pt of cumulativeSeries) {
            peak = Math.max(peak, pt.cumulative);
            maxDd = Math.min(maxDd, pt.cumulative - peak);
        }
        return Number(maxDd.toFixed(2));
    }, [cumulativeSeries]);

    const ColumnSettingsModal = () => {
        const [draftColumns, setDraftColumns] = React.useState<Set<string>>(new Set(visibleColumns));
        const [showMaxColumnMessage, setShowMaxColumnMessage] = React.useState(false);
        const modalRef = React.useRef<HTMLDivElement | null>(null);

        React.useEffect(() => {
            if (isColumnSettingsOpen) setDraftColumns(new Set(visibleColumns));

            const handleClickOutside = (event: MouseEvent) => {
                if (isColumnSettingsOpen && modalRef.current && !modalRef.current.contains(event.target as Node)) {
                    setIsColumnSettingsOpen(false);
                }
            };
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }, [isColumnSettingsOpen, visibleColumns]);

        React.useEffect(() => {
            if (showMaxColumnMessage) {
                const timer = setTimeout(() => setShowMaxColumnMessage(false), 3000);
                return () => clearTimeout(timer);
            }
            return;
        }, [showMaxColumnMessage]);

        const handleToggle = (id: string) => {
            setDraftColumns((prev) => {
                const newSet = new Set(prev);
                if (newSet.has(id)) {
                    newSet.delete(id);
                } else if (newSet.size < MAX_VISIBLE_COLUMNS) {
                    newSet.add(id);
                } else {
                    setShowMaxColumnMessage(true);
                }
                return newSet;
            });
        };

        const handleSave = () => {
            setVisibleColumns(Array.from(draftColumns));
            setIsColumnSettingsOpen(false);
        };

        const handleReset = () => {
            setDraftColumns(new Set(DEFAULT_COLUMNS));
        };

        if (!isColumnSettingsOpen) return null;
        const dark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
        const supportedColumns = ALL_COLUMNS.filter(isColumnSupported);
        const unlockedColumns = supportedColumns.filter((col) => !col.locked);
        const pendingColumns = supportedColumns.filter((col) => col.locked);

        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/50" onClick={() => setIsColumnSettingsOpen(false)} />
                <div ref={modalRef} className={`${dark ? 'bg-zinc-900 border border-zinc-800 text-zinc-100' : 'bg-white'} rounded-lg shadow-xl w-full max-w-2xl relative`}>
                    <div className={`p-4 flex justify-between items-center ${dark ? 'border-b border-zinc-800' : 'border-b border-zinc-200'}`}>
                        <h2 className="text-lg font-semibold">Customize Columns</h2>
                        <button aria-label="Close" onClick={() => setIsColumnSettingsOpen(false)} className={`p-1 rounded-full ${dark ? 'hover:bg-zinc-800' : 'hover:bg-zinc-100'}`}>
                            <XIcon className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="p-4">
                        <p className={`${dark ? 'text-zinc-300' : 'text-zinc-600'} text-sm mb-4`}>Choose the columns to display in the Journal Log.</p>
                        <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                            {unlockedColumns.map((col) => {
                                const isChecked = draftColumns.has(col.id);
                                const isDisabled = !isChecked && draftColumns.size >= MAX_VISIBLE_COLUMNS;
                                return (
                                    <label key={col.id} className={`flex items-center space-x-2 ${isDisabled && !isChecked ? 'cursor-not-allowed' : ''}`}>
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => handleToggle(col.id)}
                                            disabled={isDisabled}
                                            className="h-4 w-4 rounded border-zinc-300 accent-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:accent-zinc-500"
                                        />
                                        <span className={`text-sm ${isDisabled ? 'text-zinc-400' : dark ? 'text-zinc-200' : ''}`}>{col.label}</span>
                                    </label>
                                );
                            })}
                        </div>
                        <div className="mt-5">
                            <div className={`text-[11px] uppercase tracking-[0.22em] font-semibold ${dark ? 'text-zinc-400' : 'text-zinc-500'} mb-2`}>Pending Integration</div>
                            <div className={`${dark ? 'border-t border-zinc-800' : 'border-t border-zinc-200'} pt-3`}>
                                <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                                    {pendingColumns.map((col) => {
                                        const isChecked = draftColumns.has(col.id);
                                        return (
                                            <label key={col.id} className="flex items-center space-x-2 cursor-not-allowed">
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={() => handleToggle(col.id)}
                                                    disabled
                                                    className="h-4 w-4 rounded border-zinc-300 accent-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:accent-zinc-500"
                                                />
                                                <span className={`text-sm ${dark ? 'text-zinc-200' : ''}`}>{col.label}</span>
                                                <LockIcon className="w-3 h-3 text-zinc-400" />
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                        {(showMaxColumnMessage || draftColumns.size >= MAX_VISIBLE_COLUMNS) && (
                            <p className="text-sm text-red-500 mt-3">Maximum of 12 columns can be selected.</p>
                        )}
                    </div>
                    <div className={`p-4 flex justify-end items-center gap-2 ${dark ? 'border-t border-zinc-800' : 'border-t border-zinc-200'}`}>
                        <button onClick={() => setIsColumnSettingsOpen(false)} className={`px-4 py-2 rounded-lg text-sm ${dark ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-zinc-100 hover:bg-zinc-200'}`}>Cancel</button>
                        <button onClick={handleReset} className={`px-4 py-2 rounded-lg text-sm ${dark ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-zinc-100 hover:bg-zinc-200'}`}>Reset to Defaults</button>
                        <button onClick={handleSave} className="px-4 py-2 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700">Save changes</button>
                    </div>
                </div>
            </div>
        );
    };

    const CustomEntryModal = () => {
        const now = Date.now();
        const defaultTs = baseDate ? (getStartOfDayLocal(baseDate) + (now - getStartOfDayLocal(new Date()))) : now;

        const [symbol, setSymbol] = React.useState('');
        const [side, setSide] = React.useState<'Long' | 'Short' | ''>('');
        const [pnlStr, setPnlStr] = React.useState('');
        const [volumeStr, setVolumeStr] = React.useState('');
        const [feesStr, setFeesStr] = React.useState('');
        const [commissionStr, setCommissionStr] = React.useState('');
        const [durationStr, setDurationStr] = React.useState('');
        const [notes, setNotes] = React.useState('');

        if (!isCustomEntryOpen) return null;

        const onSubmit = (e: React.FormEvent) => {
            e.preventDefault();
            const cleanSymbol = symbol.trim().toUpperCase();
            const pnl = Number(pnlStr);
            const volume = Math.abs(Math.round(Number(volumeStr) || 0));
            const durationMinutes = Number.isFinite(Number(durationStr)) ? Number(durationStr) : undefined;
            const fees = Number.isFinite(Number(feesStr)) ? Number(feesStr) : 0;
            const commission = Number.isFinite(Number(commissionStr)) ? Number(commissionStr) : 0;
            if (!cleanSymbol || !Number.isFinite(pnl)) return;
            const id = `${defaultTs}_${cleanSymbol}_${Math.random().toString(16).slice(2)}`;
            setCustomRows((prev) => [...prev, { id, timestamp: defaultTs, symbol: cleanSymbol, volume, side, pnl, durationMinutes, fees, commission, notes }]);
            setIsCustomEntryOpen(false);
        };

        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/50" onClick={() => setIsCustomEntryOpen(false)} />
                <div className="relative w-full max-w-lg rounded-2xl border border-zinc-200 dark:border-gray-800 bg-white dark:bg-[#1a1a1c] p-6 shadow-2xl text-zinc-900 dark:text-white">
                    <div className="flex items-start justify-between mb-4">
                        <div>
                            <div className="text-lg font-semibold text-zinc-900 dark:text-white">Custom Entry</div>
                            <div className="text-xs text-zinc-600 dark:text-gray-400">Adds a row for this day</div>
                        </div>
                        <button
                            type="button"
                            className="px-3 py-1.5 text-xs bg-zinc-100 hover:bg-zinc-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg text-zinc-800 dark:text-gray-200 transition-colors"
                            onClick={() => setIsCustomEntryOpen(false)}
                        >
                            Close
                        </button>
                    </div>

                    <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="md:col-span-2">
                            <label className="block text-xs text-zinc-600 dark:text-gray-400 mb-1">Time</label>
                            <div className="px-3 h-9 rounded-lg border border-zinc-200 dark:border-gray-800 bg-white dark:bg-[#141416] flex items-center text-sm text-zinc-800 dark:text-gray-200">
                                {fmtTime24hWithSeconds(defaultTs, displayTimeZone)}
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs text-zinc-600 dark:text-gray-400 mb-1">Symbol</label>
                            <input
                                value={symbol}
                                onChange={(e) => setSymbol(e.target.value)}
                                className="w-full px-3 h-9 rounded-lg border border-zinc-200 dark:border-gray-800 bg-white dark:bg-[#141416] text-sm text-zinc-900 dark:text-white"
                                placeholder="META"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-zinc-600 dark:text-gray-400 mb-1">Side</label>
                            <select
                                value={side}
                                onChange={(e) => setSide(e.target.value as any)}
                                className="w-full px-3 h-9 rounded-lg border border-zinc-200 dark:border-gray-800 bg-white dark:bg-[#141416] text-sm text-zinc-900 dark:text-white"
                            >
                                <option value="">Auto</option>
                                <option value="Long">Long</option>
                                <option value="Short">Short</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs text-zinc-600 dark:text-gray-400 mb-1">Volume</label>
                            <input
                                value={volumeStr}
                                onChange={(e) => setVolumeStr(e.target.value)}
                                className="w-full px-3 h-9 rounded-lg border border-zinc-200 dark:border-gray-800 bg-white dark:bg-[#141416] text-sm text-zinc-900 dark:text-white"
                                placeholder="671"
                                inputMode="numeric"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-zinc-600 dark:text-gray-400 mb-1">P&L</label>
                            <input
                                value={pnlStr}
                                onChange={(e) => setPnlStr(e.target.value)}
                                className="w-full px-3 h-9 rounded-lg border border-zinc-200 dark:border-gray-800 bg-white dark:bg-[#141416] text-sm text-zinc-900 dark:text-white"
                                placeholder="170.37"
                                inputMode="decimal"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-zinc-600 dark:text-gray-400 mb-1">Fees</label>
                            <input
                                value={feesStr}
                                onChange={(e) => setFeesStr(e.target.value)}
                                className="w-full px-3 h-9 rounded-lg border border-zinc-200 dark:border-gray-800 bg-white dark:bg-[#141416] text-sm text-zinc-900 dark:text-white"
                                placeholder="0.75"
                                inputMode="decimal"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-zinc-600 dark:text-gray-400 mb-1">Commission</label>
                            <input
                                value={commissionStr}
                                onChange={(e) => setCommissionStr(e.target.value)}
                                className="w-full px-3 h-9 rounded-lg border border-zinc-200 dark:border-gray-800 bg-white dark:bg-[#141416] text-sm text-zinc-900 dark:text-white"
                                placeholder="0.75"
                                inputMode="decimal"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-zinc-600 dark:text-gray-400 mb-1">Duration (minutes)</label>
                            <input
                                value={durationStr}
                                onChange={(e) => setDurationStr(e.target.value)}
                                className="w-full px-3 h-9 rounded-lg border border-zinc-200 dark:border-gray-800 bg-white dark:bg-[#141416] text-sm text-zinc-900 dark:text-white"
                                placeholder="15"
                                inputMode="numeric"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-xs text-zinc-600 dark:text-gray-400 mb-1">Notes</label>
                            <input
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className="w-full px-3 h-9 rounded-lg border border-zinc-200 dark:border-gray-800 bg-white dark:bg-[#141416] text-sm text-zinc-900 dark:text-white"
                                placeholder=""
                            />
                        </div>
                        <div className="md:col-span-2 flex justify-end">
                            <button
                                type="submit"
                                className="px-4 h-9 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-colors"
                            >
                                Add Entry
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    // --- CHART Mockup (Simplified for component) ---
    const CumulativePnlChart = () => {
        const axisTickFill = isDark ? '#ffffff' : '#000000';
        const axisStroke = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(24,24,27,0.12)';
        const gridStroke = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(24,24,27,0.08)';
        const tooltipBg = isDark ? 'rgba(26,26,28,0.96)' : '#fff';
        const tooltipBorder = isDark ? '1px solid rgba(255,255,255,0.10)' : '1px solid rgba(24,24,27,0.08)';
        const tooltipText = isDark ? 'rgba(255,255,255,0.9)' : 'rgba(24,24,27,0.9)';
        const tooltipSubText = isDark ? 'rgba(255,255,255,0.6)' : 'rgba(24,24,27,0.45)';
        const cursorStroke = isDark ? 'rgba(255,255,255,0.16)' : 'rgba(24,24,27,0.12)';

        const YTick = ({ x, y, payload }: any) => {
            const v = Number(payload.value);
            const color = v > 0 ? '#22C55E' : (v < 0 ? '#EF4444' : axisTickFill);
            return (
                <text x={(x as number) - 8} y={(y as number) + 4} fill={color} fontSize={13} textAnchor="end">{`$${Number(v).toFixed(0)}`}</text>
            );
        };

        return (
            <div className="bg-white dark:bg-[#1a1a1c] p-4 rounded-xl border border-zinc-200 dark:border-gray-800 shadow-md col-span-1 md:col-span-2">
                <div className="flex items-center mb-4">
                    <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">Cumulative P&L by Trade</h2>
                </div>
                <div className="h-64 flex flex-col justify-end overflow-hidden">
                    <div className="relative w-full h-full">
                                {cumulativeSeries.length ? (
                            <ResponsiveContainer width="100%" height="100%">
                                {(() => {
                                    const dataMax = Math.max(0, ...cumulativeSeries.map((pt) => pt.cumulative));
                                    const dataMin = Math.min(0, ...cumulativeSeries.map((pt) => pt.cumulative));
                                    const offset = dataMax === dataMin
                                        ? 0
                                        : (dataMax / (dataMax - dataMin));

                                    return (
                                        <AreaChart data={cumulativeSeries} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                                            <defs>
                                                <linearGradient id="journalCumPnlStroke" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor={'#22C55E'} stopOpacity={1} />
                                                    <stop offset={`${offset * 100}%`} stopColor={'#22C55E'} stopOpacity={1} />
                                                    <stop offset={`${offset * 100}%`} stopColor={'#EF4444'} stopOpacity={1} />
                                                    <stop offset="100%" stopColor={'#EF4444'} stopOpacity={1} />
                                                </linearGradient>
                                                <linearGradient id="journalCumPnlFill" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor={'#22C55E'} stopOpacity={0.4} />
                                                    <stop offset={`${offset * 100}%`} stopColor={'#22C55E'} stopOpacity={0.35} />
                                                    <stop offset={`${offset * 100}%`} stopColor={'#EF4444'} stopOpacity={0.3} />
                                                    <stop offset="100%" stopColor={'#EF4444'} stopOpacity={0.08} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid stroke={gridStroke} vertical={false} />
                                            <ReferenceLine y={0} stroke={axisStroke} />
                                            <XAxis
                                                dataKey="trade"
                                                tick={{ fill: axisTickFill, fontSize: 13 }}
                                                axisLine={{ stroke: axisStroke }}
                                                tickLine={false}
                                                minTickGap={14}
                                            />
                                            <YAxis
                                                tick={<YTick />}
                                                axisLine={{ stroke: axisStroke }}
                                                tickLine={false}
                                                width={56}
                                            />
                                    {
                                        // Custom tooltip to color positive numbers green and negative red
                                    }
                                    <Tooltip
                                        cursor={{ stroke: cursorStroke, strokeDasharray: '3 3' }}
                                        contentStyle={{ background: tooltipBg, border: tooltipBorder, borderRadius: 10 }}
                                        labelStyle={{ color: tooltipText }}
                                        content={({ active, payload, label }: any) => {
                                            if (!active || !payload || !payload.length) return null;
                                            const data = payload[0].payload || {};
                                            const cumulative = Number.isFinite(data.cumulative) ? Number(data.cumulative) : 0;
                                            const pnl = Number.isFinite(data.pnl) ? Number(data.pnl) : 0;
                                            const green = '#22C55E';
                                            const red = '#EF4444';
                                            const labelText = `Trade ${label}`;
                                            return (
                                                <div style={{ background: tooltipBg, border: tooltipBorder, borderRadius: 10, padding: 10, color: tooltipText }}>
                                                    <div style={{ fontSize: 12, marginBottom: 6 }}>{labelText}</div>
                                                    <div style={{ fontSize: 13 }}>
                                                        <span style={{ color: cumulative >= 0 ? green : red, fontWeight: 600 }}>${cumulative.toFixed(2)}</span>
                                                        <span style={{ color: tooltipSubText, marginLeft: 8 }}>Cumulative</span>
                                                    </div>
                                                    <div style={{ fontSize: 13, marginTop: 6 }}>
                                                        <span style={{ color: pnl >= 0 ? green : red, fontWeight: 600 }}>${pnl.toFixed(2)}</span>
                                                        <span style={{ color: tooltipSubText, marginLeft: 8 }}>P&L</span>
                                                    </div>
                                                </div>
                                            );
                                        }}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="cumulative"
                                        stroke="url(#journalCumPnlStroke)"
                                        strokeWidth={2}
                                        fill="url(#journalCumPnlFill)"
                                        dot={false}
                                        isAnimationActive={false}
                                    />
                                </AreaChart>
                                    );
                                })()}
                            </ResponsiveContainer>
                        ) : (
                            <div className="absolute inset-0 rounded-lg flex items-center justify-center text-sm text-zinc-500 dark:text-gray-500">
                                No trades for this day
                            </div>
                        )}
                        <div className="absolute bottom-2 right-2 text-xs text-zinc-500 dark:text-gray-500">{totalTrades}</div>
                    </div>
                </div>
                <div className="text-xs text-zinc-500 dark:text-gray-500 mt-2 border-t border-zinc-200 dark:border-gray-800 pt-2">
                    P&L measured against zero baseline for all trades today.
                </div>
            </div>
        );
    };

    const TradesTable = () => (
        <div className="rounded-xl bg-transparent">
            <div className="p-0">
                <div className="flex items-center justify-between rounded-t-xl border border-b-0 border-zinc-200 bg-white p-4 dark:border-gray-800 dark:bg-[#1d1d20]">
                <button
                    type="button"
                    onClick={() => setIsCustomEntryOpen(true)}
                    className="flex items-center gap-2 px-3 h-9 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    <span>Create Custom Entry</span>
                </button>
                <button
                    type="button"
                    onClick={() => setIsColumnSettingsOpen(true)}
                    className="flex items-center gap-2 px-3 h-9 rounded-lg border text-sm font-semibold bg-zinc-100 text-zinc-800 border-zinc-200 hover:bg-zinc-50 dark:bg-[#1a1a1c] dark:border-gray-700 dark:text-gray-100 dark:hover:bg-[#222226] transition-colors"
                >
                    <Settings className="w-4 h-4" />
                    <span>Column Settings</span>
                </button>
            </div>

            <div ref={tableScrollerRef} className="scrollable relative z-0 overflow-x-auto rounded-b-xl border border-zinc-200 bg-white dark:border-gray-800 dark:bg-[#1d1d20]">
                    <table className="text-sm table-fixed border-separate border-spacing-0" style={{ width: tableWidthPx ? `${tableWidthPx}px` : '100%', minWidth: '100%' }}>
                    <colgroup>
                        {tableColumns.map((col) => (
                            <col key={col.id} style={columnWidths[col.id] ? { width: `${columnWidths[col.id]}px` } : undefined} />
                        ))}
                    </colgroup>
                    <thead className="border-b border-zinc-200 dark:border-gray-800/80 select-none">
                        <tr className="bg-zinc-100 dark:bg-[#222226]">
                            {tableColumns.map((col) => (
                                <JournalHeader key={col.id} col={col} />
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length ? rows.map((trade) => (
                            <tr key={trade.key} className="border-b border-zinc-200 dark:border-gray-800/80 hover:bg-zinc-50 dark:hover:bg-[#222226]/40 transition-colors">
                                {tableColumns.map((col) => (
                                    <td
                                        key={col.id}
                                        className="px-4 py-3 text-center border-r border-b border-zinc-200 dark:border-gray-800 first:border-l first:border-zinc-200 dark:first:border-gray-800 text-zinc-700 dark:text-gray-300"
                                        style={columnWidths[col.id] ? { width: `${columnWidths[col.id]}px` } : undefined}
                                    >
                                        {col.id === 'time' && fmtTime24hWithSeconds(trade.timestamp, displayTimeZone)}
                                        {col.id === 'symbol' && <span className="font-semibold text-zinc-900 dark:text-gray-100">{trade.symbol}</span>}
                                        {col.id === 'volume' && (trade.volume || '—')}
                                        {col.id === 'side' && (
                                            <span className={`inline-flex items-center justify-center gap-2 px-2 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap leading-none border ${
                                                String(trade.side).toLowerCase().startsWith('s')
                                                    ? 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700'
                                                    : 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700'
                                            }`}>
                                                {String(trade.side || '').toUpperCase()}
                                            </span>
                                        )}
                                        {col.id === 'status' && (
                                            <span
                                                className={`inline-flex items-center justify-center gap-2 px-2 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap leading-none border ${
                                                    trade.status === 'open'
                                                        ? 'bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-700'
                                                        : 'bg-zinc-100 text-zinc-600 border-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700'
                                                }`}
                                                title={trade.status === 'open' ? 'Open trade' : 'Closed trade'}
                                            >
                                                {trade.status === 'open' ? 'OPEN' : 'CLOSED'}
                                            </span>
                                        )}
                                        {col.id === 'pnl' && (trade.status === 'open' ? (
                                            <span className="font-semibold text-zinc-400 dark:text-gray-500" title="Open trade — realized P&L unavailable">—</span>
                                        ) : (
                                            <span className={`font-semibold ${trade.pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>{fmtMoney(trade.pnl)}</span>
                                        ))}
                                        {col.id === 'commissionAndFees' && fmtMoney((Number(trade.fees) || 0) + (Number(trade.commission) || 0))}
                                        {col.id === 'duration' && fmtDurationMinutes(trade.durationMinutes)}
                                        {col.id === 'notes' && (trade.notes || '')}
                                        {col.id === 'tags' && (
                                            Array.isArray((trade as any).tags) && (trade as any).tags.length ? (
                                                <div className="flex flex-wrap items-center justify-center gap-3">
                                                    {(trade as any).tags.slice(0, 6).map((tag: string) => (
                                                        <RemovableTag
                                                            key={tag}
                                                            label={tag}
                                                            onRemove={() => handleRemoveTag(trade.key, tag)}
                                                        />
                                                    ))}
                                                    {(trade as any).tags.length > 6 ? (
                                                        <span className="inline-flex items-center justify-center rounded-md border border-[#2c4476] bg-[#19233c] px-2.5 py-1.5 text-xs font-semibold text-[#93bbf6]">
                                                            +{(trade as any).tags.length - 6}
                                                        </span>
                                                    ) : null}
                                                </div>
                                            ) : (
                                                <span className="text-zinc-500 dark:text-gray-500">—</span>
                                            )
                                        )}
                                    </td>
                                ))}
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={tableColumns.length} className="px-4 py-10 text-center text-zinc-500 dark:text-gray-500">
                                    No trades for this day
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                {draggingColumnId && dropIndicator ? (
                    <div
                        className="pointer-events-none absolute top-0 bottom-0 z-30"
                        style={{ left: dropIndicator.left, width: 2, background: 'rgba(59,130,246,0.9)', transition: 'left 120ms linear' }}
                    />
                ) : null}
            </div>

            <ColumnSettingsModal />
            <CustomEntryModal />
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-zinc-100 px-4 py-8 text-zinc-900 dark:bg-zinc-950 dark:text-white sm:px-6 lg:px-8">
            
            {/* --- TOP RIBBON --- */}
            <header className="grid grid-cols-[1fr_auto_1fr] items-center mb-6 gap-4">
                <h1 className="text-2xl font-bold text-zinc-900 dark:text-white justify-self-start">{headerDate}</h1>
                <div className="flex items-center gap-3 justify-self-center">
                    <button
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-colors shadow-md min-w-max whitespace-nowrap bg-zinc-100 text-zinc-800 hover:bg-zinc-50 border border-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700 dark:border-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => {
                            if (canNavigateByDate && onNavigateDate && baseDate) onNavigateDate(shiftDate(baseDate as Date, -1));
                        }}
                        disabled={!canNavigateByDate}
                    >
                        <ChevronLeft className="w-3 h-3" /> Previous Day
                    </button>
                    <button
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-colors shadow-md min-w-max whitespace-nowrap bg-zinc-100 text-zinc-800 hover:bg-zinc-50 border border-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700 dark:border-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => {
                            if (canNavigateByDate && onNavigateDate && baseDate) onNavigateDate(shiftDate(baseDate as Date, 1));
                        }}
                        disabled={!canNavigateByDate}
                    >
                        Next Day <ChevronRight className="w-3 h-3" />
                    </button>
                </div>
                <div aria-hidden="true" />
            </header>

            <div className="space-y-6">
                
                {/* --- 1. KEY METRICS ROW --- */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <MetricCard 
                        title="Gross P&L"
                        value={grossPnl}
                        subtext="P&L before fees & commissions"
                        isNegative={grossPnl < 0}
                    />
                     <MetricCard 
                        title="Net P&L"
                        value={netPnl}
                        subtext="Final profit after expenses"
                        isNegative={isNetPnlNegative}
                    />
                    <MetricCard 
                        title="Total Trades"
                        value={totalTrades}
                        subtext={`${winners} Winners, ${losers} Losers`}
                        isNegative={false}
                        isCurrency={false}
                    />
                    <MetricCard 
                        title="Win Rate %"
                        value={`${winRate.toFixed(1)}%`}
                        subtext="Winning trade frequency"
                        isNegative={winRate < 50}
                    />
                </div>

                {/* --- 2. MAIN CHART & TRADE SUMMARY --- */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    
                    {/* Cumulative P&L Chart */}
                    <CumulativePnlChart />

                    {/* Trade & Volume Metrics Column */}
                    <div className="grid grid-cols-2 md:grid-cols-1 gap-6">
                        
                        {/* Win/Loss Breakdown */}
                        <div className="bg-white dark:bg-[#1a1a1c] p-4 rounded-xl border border-zinc-200 dark:border-gray-800 shadow-md">
                            <h3 className="text-sm font-medium text-zinc-700 dark:text-gray-400 mb-3">Trade Breakdown</h3>
                            <div className="relative flex justify-around items-center">
                                <div className="pointer-events-none absolute left-1/2 top-4 bottom-4 w-px -translate-x-1/2 bg-zinc-200 dark:bg-gray-800/60" />
                                <ProgressRing 
                                    percentage={winRate} 
                                    color="text-green-500" 
                                    label={`Winners: ${winners}`} 
                                />
                                <ProgressRing 
                                    percentage={lossRate} 
                                    color="text-red-500" 
                                    label={`Losers: ${losers}`} 
                                />
                            </div>
                        </div>

                        {/* Volume / Max Drawdown */}
                        <div className="bg-white dark:bg-[#1a1a1c] p-4 rounded-xl border border-zinc-200 dark:border-gray-800 shadow-md">
                            <h3 className="text-sm font-medium text-zinc-700 dark:text-gray-400 mb-3">Execution Metrics</h3>
                            <div className="grid grid-cols-3 divide-x divide-zinc-200 dark:divide-gray-800 text-center items-center">
                                <div className="px-4 py-2">
                                    <div className="text-sm text-zinc-600 dark:text-gray-400 mb-1">Volume</div>
                                    <div className="text-3xl font-bold text-zinc-900 dark:text-white">{volume}</div>
                                </div>
                                <div className="px-4 py-2">
                                    <div className="text-sm text-zinc-600 dark:text-gray-400 mb-1">Fees/Comm.</div>
                                    <div className="text-3xl font-bold text-zinc-900 dark:text-white">${(fees + commission).toFixed(2)}</div>
                                </div>
                                <div className="px-4 py-2">
                                    <div className="text-sm text-zinc-600 dark:text-gray-400 mb-1">Drawdown</div>
                                    <div className="text-3xl font-bold text-red-500">${maxDrawdown}</div>
                                </div>
                            </div>
                        </div>
                        
                    </div>
                </div>

                {/* --- 3. TRADES TABLE --- */}
                <TradesTable />

            </div>
        </div>
    );
};

export default JournalEntryPage;
// Also provide a named export to match existing imports elsewhere in the app
export { JournalEntryPage };
